import React, { useCallback, useEffect, useState } from 'react'
import SearchList, { SearchListWrapper } from '../components/SearchList'
import LabelInput from '../components/LabelInput'
import RowRadioButton from '../components/RowRadioButton'
import LabelOnly from '../components/LabelOnly'
import InputOnly from '../components/InputOnly'
import ColumnRadioWith3M from '../components/ColumnRadioWith3M'
import { TitleButton } from '../components/TitleButton'
import PatternLabelHeader from '../components/PatternLabelHeader'
import PatternSelection from '../components/PatternSelection'
import MonthLineGraph2 from '../components/graph/MonthLineGraph2'
import { useLazyQuery, useMutation, useQuery } from '@apollo/client'
import { customerStatusFunc } from '../util/customerStatusFunc'
import { ISeasonalNum } from '../util/algorithm/algoInterface'
import { calSeasonalSum } from '../util/algorithm/monthToSeason'
import { seasonMapping } from '../util/SeasonMapping'
import { divTimeData } from '../util/algorithm/classifyTimeData'
import { calMonthRepPattern } from '../util/algorithm/calRepPattern'
import {
  Body,
  BodyWrapper,
  ContentWrapper,
  DetailColWrapper,
  DetailWrapper,
  GraphBody,
  GraphWrapper,
  Header,
  InputItem,
  InputLabel,
  InputWrapper,
  Left,
  MonthChip,
  MonthChipText,
  MonthChipWrapper,
  PatternSelectWrapper,
  Right,
  SeasonBox,
  SeasonFrame,
  SeasonTextBox,
  Text,
  TextBox,
  TextBox2,
  TextWrapper,
  TradingMethod,
  TradingMethodBody,
  TradingMethodDescription,
  TradingMethodDescriptionFormula,
  TradingMethodDescriptionText,
  TradingMethodDescriptionTitle,
} from '../style/CustomerPV.styled'
import {
  CreatePVcustGQL,
  DeletePVcustGQL,
  findAllPVcusGQL,
  findAllPVPatternNamesGQL,
  findOnePVcusGQL,
  findPVPatternDataGQL,
  UpdatePVcustGQL,
} from '../gql/CustomerPV.gql'
import {
  PvRevenueContractType,
  getPreviousMethodFromRevenueContractType,
  getPvRevenueContractType,
  getRevenueContractUnitPriceLabel,
  isUnitPriceRevenueContract,
} from '../util/algorithm/pvRevenue'

export interface ICustomerPVData {
  name: string | string[]
  facilityCapacity: number
  previousMethod: string
  revenueContractType?: PvRevenueContractType
  contractPrice: number
  RECWeight: number
  pvOpt: string
  patternList: any[]
  _id?: string
  index?: string
}

export const initializecustomerPVData = (): ICustomerPVData => {
  return {
    name: '',
    facilityCapacity: 0,
    previousMethod: 'fixedPrice',
    revenueContractType: 'fixedPriceContract',
    contractPrice: 0,
    RECWeight: 1,
    pvOpt: 'realTime',
    patternList: [
      { patternId: '', months: [1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12] },
    ],
  }
}

const formatPvCapacity = (pv: Partial<ICustomerPVData>) => {
  const facilityCapacity = Number(pv.facilityCapacity) || 0

  return `${facilityCapacity.toLocaleString()}kW`
}

const getPvRevenueContractListLabel = (
  revenueContractType: PvRevenueContractType
) => {
  switch (revenueContractType) {
    case 'kepcoPpa':
      return '한전 PPA'
    case 'marketSmp':
      return '전력시장 직접 판매'
    case 'directPpa':
      return '제3자 PPA'
    case 'fixedPriceContract':
    default:
      return 'RPS 고정가격계약'
  }
}

const formatPvListDescription = (pv: Partial<ICustomerPVData>) => {
  const revenueContractType = getPvRevenueContractType(pv)

  return `${formatPvCapacity(pv)} · ${getPvRevenueContractListLabel(
    revenueContractType
  )}`
}

const formatGenerationHours = (
  generation?: number,
  dayCount?: number,
  facilityCapacity?: number
) => {
  if (!generation || !facilityCapacity || !dayCount) return '0시간'

  const hours = generation / (facilityCapacity * dayCount)
  return `${Number(hours.toFixed(1)).toLocaleString()}시간`
}

const pvRevenueContractDescriptions: Record<
  PvRevenueContractType,
  {
    title: string
    description?: string
    formula?: string
  }
> = {
  kepcoPpa: {
    title: '한전 PPA',
    description:
      '한전에 재생에너지 전력을 팔고, 월 가중평균SMP에 따라 정산을 받는 구조입니다.',
    formula: '월 가중평균SMP 단가 * 발전량 + REC 단가 * 발전량',
  },
  marketSmp: {
    title: '전력시장 직접 판매',
    description:
      'KPX에 재생에너지 전력을 팔고, 그에 따라 SMP 평균단가에 따라 정산을 받는 구조입니다.',
    formula: '시간대별 SMP 단가 * 발전량 + REC * 발전량',
  },
  fixedPriceContract: {
    title: 'RPS 고정가격계약',
    description:
      '공급의무자와 장기계약을 맺고, SMP와 REC를 합산한 고정가격 기준으로 정산을 받는 구조입니다.',
    formula: '고정계약단가 * 발전량',
  },
  directPpa: {
    title: '제3자 PPA',
    description:
      '한전을 통해서 기업에게 재생에너지를 가상으로 판매하는 방식입니다.',
    formula: '기업과 맺은 PPA단가 * 실제 기업구매발전량',
  },
}

export const CustomerPV = () => {
  const [PVData, setPVData] = useState<ICustomerPVData>(
    initializecustomerPVData
  )

  // [수정 1] data, loading, error, onCompleted 추가 (디버깅 및 로직용)
  const [findOnePVData, { data: queryData, refetch, loading, error }] =
    useLazyQuery(findOnePVcusGQL, {})

  const { data: PVPatternListData } = useQuery(findAllPVPatternNamesGQL)
  const [PVPatternList, setPVPatternList] = useState<any>()
  const [createPVcust] = useMutation(CreatePVcustGQL)
  const [updatePVcust] = useMutation(UpdatePVcustGQL)
  const [deletePVcust] = useMutation(DeletePVcustGQL)
  const [isModify, setIsModify] = useState(false)
  const [radioValue, setRadioValue] = useState('0')
  const [pvOptRadio, setPvOptRadio] = useState('0')
  const [selectedPatternId, setSelectedPatternId] = useState('')
  const [selectedMonths, setSelectedMonths] = useState<number[]>([1])
  const [pvGraph, setPvGraph] = useState<any>([])

  const [findPatternData] = useLazyQuery(findPVPatternDataGQL)
  const [PatternData, setPatternData] = useState<any>()
  const activePattern = PVData.patternList?.[0]
  const activePatternId = activePattern?.patternId || ''
  const activePatternMonthsKey =
    activePattern?.months && activePattern.months.length > 0
      ? activePattern.months.join(',')
      : '1,2,3,4,5,6,7,8,9,10,11,12'

  const [seasonSum, setSeasonSum] = useState<ISeasonalNum>({
    spr: 0,
    sum: 0,
    fall: 0,
    win: 0,
  })
  const [seasonDayCounts, setSeasonDayCounts] = useState<ISeasonalNum>({
    spr: 0,
    sum: 0,
    fall: 0,
    win: 0,
  })

  const [listRefetch, setListRefetch] = useState<() => void>(() => () => {})

  const handleRefetch = useCallback(() => {
    if (listRefetch) {
      listRefetch()
    }
  }, [listRefetch])

  useEffect(() => {
    // 1. 데이터가 있는지 확인
    let sourceData = null

    if (queryData) {
      sourceData = queryData.findOnePVcust
    }

    // 2. 데이터가 있으면 "깊은 복사" 후 가공
    if (sourceData) {
      // [중요] Apollo Read-only 에러 방지를 위해 깊은 복사 수행
      // structuredClone은 최신 브라우저/Node 환경에서 지원됨.
      // 만약 구형 환경 지원이 필요하면 JSON.parse(JSON.stringify(sourceData)) 사용
      const loadedData: ICustomerPVData = JSON.parse(JSON.stringify(sourceData))

      // __typename 제거 (JSON 변환 과정에서 자동 제거되지는 않으므로 수동 제거하거나, 그냥 둬도 무방할 수 있음.
      // 보통은 저장 시점에 제거하지만, 깔끔하게 여기서 제거)
      if ('__typename' in loadedData) {
        delete (loadedData as any).__typename
      }

      // 3. "단일 패턴" 규칙에 맞춰 가공
      if (!loadedData.patternList || loadedData.patternList.length === 0) {
        loadedData.patternList = [
          { patternId: '', months: [1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12] },
        ]
      } else if (loadedData.patternList.length > 1) {
        loadedData.patternList = [
          {
            patternId: loadedData.patternList[0].patternId,
            months: [1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12],
          },
        ]
      } else {
        // 기존 객체 재사용이 아니라 복사본이므로 안전하게 수정 가능
        loadedData.patternList[0].months = [
          1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12,
        ]
      }

      loadedData.contractPrice = loadedData.contractPrice ?? 0
      loadedData.RECWeight = loadedData.RECWeight ?? 1
      loadedData.pvOpt = loadedData.pvOpt ?? 'realTime'
      loadedData.revenueContractType = getPvRevenueContractType(loadedData)
      loadedData.previousMethod = getPreviousMethodFromRevenueContractType(
        loadedData.revenueContractType
      )

      // 4. State 업데이트
      setPVData(loadedData)
    }
  }, [queryData])

  useEffect(() => {
    if (PatternData) {
      const div1000 = divTimeData(PatternData, 1000)
      setSeasonSum(calSeasonalSum(div1000, seasonMapping))

      const seasonDateSets = {
        spr: new Set<string>(),
        sum: new Set<string>(),
        fall: new Set<string>(),
        win: new Set<string>(),
      }

      PatternData.forEach((item: { date?: string }) => {
        if (!item.date) return

        const date = new Date(item.date)
        if (isNaN(date.getTime())) return

        const season = seasonMapping[date.getMonth() + 1]
        seasonDateSets[season].add(item.date.substring(0, 10))
      })

      setSeasonDayCounts({
        spr: seasonDateSets.spr.size,
        sum: seasonDateSets.sum.size,
        fall: seasonDateSets.fall.size,
        win: seasonDateSets.win.size,
      })
    }
  }, [PatternData])

  useEffect(() => {
    setSelectedPatternId(PVData.patternList?.[0]?.patternId || '')
  }, [PVData.patternList])

  useEffect(() => {
    if (!PatternData) {
      setPvGraph([])
      return
    }

    const tmpData = divTimeData(PatternData, 1000)

    switch (PVData.pvOpt) {
      case 'aver':
        setPvGraph(calMonthRepPattern(tmpData, 'aver').graphData)
        break

      case 'mid':
        setPvGraph(calMonthRepPattern(tmpData, 'mid').graphData)
        break

      case 'mode':
        setPvGraph(calMonthRepPattern(tmpData, 'mode').graphData)
        break

      case 'realTime':
      default:
        setPvGraph(tmpData)
        break
    }
  }, [PVData.pvOpt, PatternData])

  useEffect(() => {
    handleResetAll()
  }, [])

  const handleResetAll = () => {
    setPVData(initializecustomerPVData())
    setPatternData(undefined)
    setSeasonSum({
      spr: 0,
      sum: 0,
      fall: 0,
      win: 0,
    })
    setSeasonDayCounts({
      spr: 0,
      sum: 0,
      fall: 0,
      win: 0,
    })
  }

  const handleFindOne = (id: string) => {
    findOnePVData({ variables: { val: id } })
  }

  useEffect(() => {
    if (!activePatternId) {
      setPatternData(undefined)
      return
    }

    customerStatusFunc(
      [
        {
          patternId: activePatternId,
          months: activePatternMonthsKey.split(',').map(Number),
        },
      ],
      findPatternData
    ).then((result) => {
      setPatternData(result)
    })
  }, [activePatternId, activePatternMonthsKey, findPatternData])

  useEffect(() => {
    if (PVPatternListData) {
      const newData = PVPatternListData.findAllPVAPINames?.map((item: any) => ({
        value: item.name,
        label: item.name,
      }))

      setPVPatternList(newData)
    }
  }, [PVPatternListData])

  useEffect(() => {
    setIsModify(!!PVData._id)
  }, [PVData._id])

  useEffect(() => {
    switch (getPvRevenueContractType(PVData)) {
      case 'kepcoPpa':
        setRadioValue('0')
        break

      case 'marketSmp':
        setRadioValue('1')
        break

      case 'fixedPriceContract':
        setRadioValue('2')
        break

      case 'directPpa':
        setRadioValue('3')
        break

      default:
        setRadioValue('0')
        break
    }
  }, [PVData])

  useEffect(() => {
    switch (PVData.pvOpt) {
      case 'realTime':
        setPvOptRadio('0')
        break

      default:
        setPvOptRadio('1')
        break
    }
  }, [PVData.pvOpt])

  const addPattern = () => {
    setPVData((prev) => {
      const updatedPVData = { ...prev }
      updatedPVData.patternList = [...updatedPVData.patternList, {}]
      return updatedPVData
    })
  }

  const removePattern = (indexToRemove: number) => {
    setPVData((prev) => {
      const updatedPVData = { ...prev }
      updatedPVData.patternList = prev.patternList.filter(
        (_, index) => index !== indexToRemove
      )
      return updatedPVData
    })
  }

  const handleDataChange = (key: string, newValue: number | string) => {
    if (typeof newValue === 'number') {
      if (isNaN(newValue)) {
        setPVData((prev) => {
          const updatedDataUnit = { ...prev, [key]: 0 }
          return updatedDataUnit
        })
      } else {
        setPVData((prev) => {
          const updatedDataUnit = { ...prev, [key]: newValue }
          return updatedDataUnit
        })
      }
    } else {
      setPVData((prev) => {
        const updatedDataUnit = { ...prev, [key]: newValue }
        return updatedDataUnit
      })
    }
  }

  const handleRadiobutton = (value: string) => {
    let revenueContractType: PvRevenueContractType = 'kepcoPpa'

    switch (value) {
      case '1':
        revenueContractType = 'marketSmp'
        break

      case '2':
        revenueContractType = 'fixedPriceContract'
        break

      case '3':
        revenueContractType = 'directPpa'
        break

      case '0':
      default:
        revenueContractType = 'kepcoPpa'
        break
    }

    setPVData((prev) => ({
      ...prev,
      revenueContractType,
      previousMethod:
        getPreviousMethodFromRevenueContractType(revenueContractType),
    }))
  }

  const handlePvOptRadiobutton = (value: string) => {
    setPVData((prev) => ({
      ...prev,
      pvOpt: value === '0' ? 'realTime' : 'aver',
    }))
  }

  const toggleMonth = (month: number) => {
    setSelectedMonths([month])
  }

  const onSave = async () => {
    try {
      // __typename 제거
      const { __typename, ...cleanData } = PVData as any
      const revenueContractType = getPvRevenueContractType(cleanData)
      const patternList =
        cleanData.patternList && cleanData.patternList.length > 0
          ? cleanData.patternList.map((pattern: any) => ({
              ...pattern,
              months:
                pattern.months && pattern.months.length > 0
                  ? pattern.months
                  : [1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12],
            }))
          : [
              {
                patternId: '',
                months: [1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12],
              },
            ]
      const saveData = {
        ...cleanData,
        patternList,
        revenueContractType,
        previousMethod:
          getPreviousMethodFromRevenueContractType(revenueContractType),
      }

      if (PVData._id) {
        // [UPDATE]
        await updatePVcust({
          variables: {
            val: saveData,
          },
        })
      } else {
        // [CREATE]
        await createPVcust({
          variables: {
            val: saveData,
          },
        })
      }

      // 데이터 갱신
      refetch()
      handleRefetch()

      // [삭제] alert('저장되었습니다.')
      // -> 팝업이 자동으로 "성공" 화면을 띄워줍니다.
    } catch (e) {
      console.error(e)
      alert('저장 중 오류가 발생했습니다.') // 실패했을 때는 알림을 띄워주는 것이 좋습니다.

      // [중요] 에러를 던져야 팝업이 성공 화면으로 넘어가지 않습니다.
      throw e
    }
  }

  const onDelete = async () => {
    if (PVData._id) {
      await deletePVcust({
        variables: {
          val: PVData._id,
        },
      })

      listRefetch()
      handleResetAll()
    }
  }

  // [디버깅용 함수 추가]
  const handleDebugFindOne = (arg: any) => {
    console.log('=== [DEBUG] SearchList 클릭 로그 ===')
    console.log('1. 넘어온 인자 값:', arg)
    console.log('2. 인자 타입:', typeof arg)

    // 에러 방지 로직: 문자열이 오면 객체로 감싸서 실행
    if (typeof arg === 'string') {
      console.log('👉 문자열 ID가 들어와서 객체로 변환하여 실행합니다.')
      findOnePVData({ variables: { val: arg } })
    } else {
      console.log('👉 객체(Variables)가 들어와서 그대로 실행합니다.')
      findOnePVData(arg)
    }
  }

  const revenueContractType = getPvRevenueContractType(PVData)
  const revenueContractDescription =
    pvRevenueContractDescriptions[revenueContractType]

  return (
    <>
      <SearchListWrapper>
        <SearchList
          onReset={handleResetAll}
          findOne={handleDebugFindOne}
          findAllListgql={findAllPVcusGQL}
          setRefetch={setListRefetch}
          nameKey="name"
          descriptionRenderer={formatPvListDescription}
        />
      </SearchListWrapper>
      <ContentWrapper>
        <Header>
          <TitleButton
            title="PV 발전사업자 정보"
            onSaveClick={onSave}
            onCancleClick={handleResetAll}
            onDeleteClick={onDelete}
            isModify={isModify}
          />
          {/* <StatusBoardPV
            name={(PVData?.name as string) || ''}
            seasonSum={seasonSum}
            seasonDayCounts={seasonDayCounts}
            facilityCapacity={PVData.facilityCapacity}
          /> */}
        </Header>
        <BodyWrapper>
          <Body>
            <Left>
              <LabelInput
                label="고객명"
                placeholder="이름을 입력하세요"
                onChange={(e) => handleDataChange('name', e.target.value)}
                value={(PVData.name as string) || ''}
              />
              <LabelInput
                label="설비용량"
                placeholder="kW"
                onChange={(e) =>
                  handleDataChange('facilityCapacity', Number(e.target.value))
                }
                value={PVData.facilityCapacity.toString()}
                type="number"
                unit="kW"
              />
              <LabelInput
                label="REC 가중치"
                placeholder="REC 가중치"
                onChange={(e) =>
                  handleDataChange('RECWeight', Number(e.target.value))
                }
                value={PVData.RECWeight.toString()}
                type="number"
              />
              <TradingMethod>
                <LabelOnly label="기존 전력거래 방식" />
                <TradingMethodBody>
                  <RowRadioButton
                    list={[
                      { label: '한전 PPA' },
                      { label: '전력시장 직접 판매' },
                      { label: 'RPS 고정가격계약' },
                      { label: '제3자 PPA' },
                    ]}
                    onChange={(value: string) => handleRadiobutton(value)}
                    value={radioValue}
                  />
                  {isUnitPriceRevenueContract(revenueContractType) && (
                    <InputWrapper>
                      <InputItem>
                        <InputLabel>
                          {getRevenueContractUnitPriceLabel(
                            revenueContractType
                          )}
                        </InputLabel>
                        <InputOnly
                          placeholder={getRevenueContractUnitPriceLabel(
                            revenueContractType
                          )}
                          value={PVData.contractPrice.toString()}
                          onChange={(e) =>
                            handleDataChange(
                              'contractPrice',
                              Number(e.target.value)
                            )
                          }
                          type="number"
                          width="140px"
                          unit="원/kWh"
                        />
                      </InputItem>
                    </InputWrapper>
                  )}
                  <TradingMethodDescription>
                    <TradingMethodDescriptionTitle>
                      {revenueContractDescription.title}
                    </TradingMethodDescriptionTitle>
                    {revenueContractDescription.description && (
                      <TradingMethodDescriptionText>
                        {revenueContractDescription.description}
                      </TradingMethodDescriptionText>
                    )}
                    {revenueContractDescription.formula && (
                      <TradingMethodDescriptionFormula>
                        {revenueContractDescription.formula}
                      </TradingMethodDescriptionFormula>
                    )}
                  </TradingMethodDescription>
                </TradingMethodBody>
              </TradingMethod>
            </Left>
            <Right>
              <PatternLabelHeader label="PV 발전량 패턴" to="/home/data-pv" />
              <PatternSelection
                title="PV 발전량 패턴 선택"
                list={PVPatternList}
                index={0}
                value={selectedPatternId}
                data={PVData}
                setData={setPVData}
                selectedValue={selectedPatternId}
                setSelectedValue={setSelectedPatternId}
              />
              <DetailColWrapper>
                <DetailWrapper $direction="column">
                  <PatternSelectWrapper>
                    <ColumnRadioWith3M
                      list={[
                        {
                          label: 'PV 원본데이터 사용',
                        },
                        {
                          label: '월별 대표 패턴 산정',
                        },
                      ]}
                      onChange={(value: string) =>
                        handlePvOptRadiobutton(value)
                      }
                      value={pvOptRadio}
                      setRadioValue={setPvOptRadio}
                      patternData={PVData.pvOpt}
                      keyName={'pvOpt'}
                      setData={setPVData}
                    />
                  </PatternSelectWrapper>
                  <GraphWrapper height="auto" $width="100%">
                    <MonthChipWrapper>
                      {Array.from({ length: 12 }, (_, index) => (
                        <MonthChip
                          key={index + 1}
                          selected={selectedMonths.includes(index + 1)}
                          onClick={() => toggleMonth(index + 1)}
                        >
                          <MonthChipText>{`${index + 1}월`}</MonthChipText>
                        </MonthChip>
                      ))}
                    </MonthChipWrapper>
                    <GraphBody>
                      <MonthLineGraph2
                        month={selectedMonths[0]}
                        data={pvGraph ? pvGraph : []}
                        width="100%"
                        height="300px"
                        selectedChartDataType={PVData.pvOpt}
                      />
                    </GraphBody>

                    <TextWrapper $align="center">
                      <Text>연간 발전량</Text>
                      <TextBox>
                        <Text fontColor="#57C9A7" fontSize="20px">
                          총
                        </Text>
                        <TextBox2>
                          <Text
                            fontColor="#57C9A7"
                            fontSize="22px"
                            fontWeight="600"
                          >
                            {Number(
                              (
                                seasonSum.spr +
                                seasonSum.sum +
                                seasonSum.fall +
                                seasonSum.win
                              ).toFixed(0)
                            ).toLocaleString()}
                          </Text>
                          <Text fontColor="#57C9A7" fontSize="18px">
                            kWh
                          </Text>
                        </TextBox2>
                      </TextBox>
                    </TextWrapper>

                    <SeasonFrame>
                      <SeasonBox>
                        <Text fontColor="#666" fontWeight="500">
                          봄
                        </Text>
                        <SeasonTextBox>
                          <TextBox2>
                            <Text
                              fontColor="#4D4D4D"
                              fontSize="15px"
                              fontWeight="500"
                            >
                              {Number(
                                seasonSum.spr.toFixed(0)
                              ).toLocaleString()}
                            </Text>
                            <Text fontColor="#666" fontWeight="500">
                              kWh
                            </Text>
                          </TextBox2>
                          <Text
                            fontColor="#666"
                            fontSize="11px"
                            fontWeight="500"
                          >
                            평균 발전시간{' '}
                            {formatGenerationHours(
                              seasonSum.spr,
                              seasonDayCounts.spr,
                              PVData.facilityCapacity
                            )}
                          </Text>
                        </SeasonTextBox>
                      </SeasonBox>
                      <SeasonBox>
                        <Text fontColor="#666" fontWeight="500">
                          여름
                        </Text>
                        <SeasonTextBox>
                          <TextBox2>
                            <Text
                              fontColor="#4D4D4D"
                              fontSize="15px"
                              fontWeight="500"
                            >
                              {Number(
                                seasonSum.sum.toFixed(0)
                              ).toLocaleString()}
                            </Text>
                            <Text fontColor="#666" fontWeight="500">
                              kWh
                            </Text>
                          </TextBox2>
                          <Text
                            fontColor="#666"
                            fontSize="11px"
                            fontWeight="500"
                          >
                            평균 발전시간{' '}
                            {formatGenerationHours(
                              seasonSum.sum,
                              seasonDayCounts.sum,
                              PVData.facilityCapacity
                            )}
                          </Text>
                        </SeasonTextBox>
                      </SeasonBox>
                      <SeasonBox>
                        <Text fontColor="#666" fontWeight="500">
                          가을
                        </Text>
                        <SeasonTextBox>
                          <TextBox2>
                            <Text
                              fontColor="#4D4D4D"
                              fontSize="15px"
                              fontWeight="500"
                            >
                              {Number(
                                seasonSum.fall.toFixed(0)
                              ).toLocaleString()}
                            </Text>
                            <Text fontColor="#666" fontWeight="500">
                              kWh
                            </Text>
                          </TextBox2>
                          <Text
                            fontColor="#666"
                            fontSize="11px"
                            fontWeight="500"
                          >
                            평균 발전시간{' '}
                            {formatGenerationHours(
                              seasonSum.fall,
                              seasonDayCounts.fall,
                              PVData.facilityCapacity
                            )}
                          </Text>
                        </SeasonTextBox>
                      </SeasonBox>
                      <SeasonBox>
                        <Text fontColor="#666" fontWeight="500">
                          겨울
                        </Text>
                        <SeasonTextBox>
                          <TextBox2>
                            <Text
                              fontColor="#4D4D4D"
                              fontSize="15px"
                              fontWeight="500"
                            >
                              {Number(
                                seasonSum.win.toFixed(0)
                              ).toLocaleString()}
                            </Text>
                            <Text fontColor="#666" fontWeight="500">
                              kWh
                            </Text>
                          </TextBox2>
                          <Text
                            fontColor="#666"
                            fontSize="11px"
                            fontWeight="500"
                          >
                            평균 발전시간{' '}
                            {formatGenerationHours(
                              seasonSum.win,
                              seasonDayCounts.win,
                              PVData.facilityCapacity
                            )}
                          </Text>
                        </SeasonTextBox>
                      </SeasonBox>
                    </SeasonFrame>
                  </GraphWrapper>
                </DetailWrapper>
              </DetailColWrapper>
            </Right>
          </Body>
        </BodyWrapper>
      </ContentWrapper>
    </>
  )
}
